import AISuggestions from '../../components/AISuggestions';
import { RRule } from 'rrule';
import React, { useState, useEffect } from 'react';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import PublishStatusComponent from '../../components/PublishStatusComponent';
import MenuItem from '@mui/material/MenuItem';
import { useToast } from '../../providers/ToastProvider';
import { Session } from '../../peer-colab-model/Session';
import { PredefinedTaskManagement, PredefinedTaskRRULAISuggest } from '../../peer-colab-model/PetKey/BackofficeFE/Client/PathItems';
import { PredefinedTask, Species } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Tasks/PathItems';
import SpeciesBreedSelector from '../../components/SpeciesBreedSelector';
import { useSpecies } from '../../providers/SpeciesProvider';
import { OperationRequest } from '@peercolab/engine';
import { onRevert } from '../../utils/GeneralHelpers';
import { PeerColabAI } from '../../peer-colab-model/interceptors/peercolab/PeerColabOperations';
import { Published } from '../../peer-colab-model/PetKey/Main/Model/1_0_Initial/Backoffice/Core/PathItems';

interface EditPredefinedTaskProps {
  id: string;
  onBack: () => void;
}

function EditPredefinedTask({ id, onBack }: EditPredefinedTaskProps) {
  // State for Add Species selector
  const [addSpeciesId, setAddSpeciesId] = useState<string | null>(null);
  const [addBreedIds, setAddBreedIds] = useState<string[]>([]);
  // Inline edit state for species rows
  const [editSpeciesId, setEditSpeciesId] = useState<string | null>(null);
  const [editBreeds, setEditBreeds] = useState<string[]>([]);
  const [addMode, setAddMode] = useState(false);
  const { species: allSpecies, loadSpecies } = useSpecies();
  const [task, setTask] = useState<PredefinedTask | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [translationKey, setTranslationKey] = useState('');
  const [iconName, setIconName] = useState('');
  const [taskSpecies, setTaskSpecies] = useState<Species[]>([]);
  const [suggestedRRULE, setSuggestedRRULE] = useState('');
  const [rruleDescriptionText, setRruleDescriptionText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [state, setState] = useState<Published>({ state: 'draft', isDirty: false });

  const { onError } = useToast();

  const loadTask = async () => {
    onError((await Session.getPredefinedTaskClient(id).request(PredefinedTaskManagement.getPredefinedTask()))
      .maybeOk((t: PredefinedTask) => {
        setTask(t);
        setName(t.name);
        setType(t.type);
        setTaskSpecies(t.species);
        setTranslationKey(t.translationKey);
        setSuggestedRRULE(t.suggestedRRULE || '');
        setIconName(t.iconName ?? '');
        setState(t.state);
        t.species.forEach(s => loadSpecies(s.speciesId));
      }));
  };

  useEffect(() => {
    loadTask();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const patchField = async <R,V>(operation: OperationRequest<unknown, R>, newValue: V, currentValue: V, revertHandler: (value: V) => void): Promise<void> => {
    if (newValue === currentValue) return
    const client = Session.getPredefinedTaskClient(id);
    onError(onRevert((await client.request(operation)), () => revertHandler(currentValue)))
      .maybeOk(loadTask);
  }

  // Species management
  async function handleAddSpecies(speciesId: string, breedIds: string[]) {
    const client = Session.getPredefinedTaskClient(id);
    await onError((await client.request(PredefinedTaskManagement.addPredefinedTaskSpecies({ speciesId, breedId: breedIds }))).maybeOk(loadTask));
  }
  async function handleUpdateSpecies(speciesId: string, breedIds: string[]) {
    const client = Session.getPredefinedTaskClient(id);
    await onError((await client.request(PredefinedTaskManagement.updatePredefinedTaskSpecies({ speciesId, breedId: breedIds }))).maybeOk(loadTask));
  }

  async function handleRemoveSpecies(speciesId: string) {
    const client = Session.getPredefinedTaskClient(id);
    await onError((await client.request(PredefinedTaskManagement.removePredefinedTaskSpecies({ speciesId }))).maybeOk(loadTask));
    setIsDeleting(false);
  }

  async function aiSuggestRRULE() {
    const result = onError((await Session.getClient().request(PeerColabAI.processChatInstructions({
      currentStateSnapshot: '',
      usageInstructions: PredefinedTaskRRULAISuggest.toAgentInstructions(),
      items: [{ type: 'message', role: 'user', content: rruleDescriptionText } ]
    }))))

    if (result.success && result.value.operations.length === 1) {
      onError((await Session.getPredefinedTaskClient(id).acceptOperation(result.value.operations[0]))
        .maybeOk(loadTask))
    }
  }

  if (!task) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <AISuggestions 
            client={Session.getPredefinedTaskClient(id)}
            currentSnapshot={JSON.stringify(task)}
            usageInstructions={PredefinedTaskManagement.toAgentInstructions()}
            developerMessage='The user is in the context of a single task that he/she is editing'
            onOperationsApplied={() => loadTask()}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Edit Predefined Task
        </Typography>
        <PublishStatusComponent
          status={state}
          onTransition={async (action) => {
            return (await Session.getPredefinedTaskClient(task.id).request(PredefinedTaskManagement.updatePredefinedTaskState({ stateTransition: action })))
              .maybeOk(() => loadTask());
          }}
        />
      </Box>
  <Box sx={{ display: 'flex', flexDirection: 'row', minWidth: 350, maxWidth: 900 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 350, maxWidth: 500 }}>
          <TextField
            label="Task Name"
            value={name}
            onChange={e => setName(e.target.value)}
            onBlur={() => patchField(PredefinedTaskManagement.updatePredefinedTaskName({ name }), name, task.name, setName)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Type"
            value={type}
            select
            onChange={e => setType(e.target.value)}
            onBlur={() => patchField(PredefinedTaskManagement.updatePredefinedTaskType({ type }), type, task.type, setType)}
            sx={{ mb: 2 }}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="health">Health</MenuItem>
            <MenuItem value="longterm">Longterm</MenuItem>
          </TextField>
          <TextField
            label="Translation Key"
            value={translationKey}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            label="Icon Name"
            value={iconName}
            onChange={e => setIconName(e.target.value)}
            onBlur={() => patchField(PredefinedTaskManagement.updatePredefinedTaskIcon({ icon: iconName }), iconName, task.iconName ?? '', setIconName)}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              label="Suggested RRULE"
              value={rruleDescriptionText}
              onChange={e => setRruleDescriptionText(e.target.value)}
              sx={{ flex: 1, mr: 2 }}
            />
            <Button variant="outlined" sx={{ minWidth: 120 }} onClick={aiSuggestRRULE}>AI Suggest</Button>
          </Box>
          {suggestedRRULE && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Raw RRULE: {suggestedRRULE}</Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                {(() => {
                  try {
                    const rule = RRule.fromString(suggestedRRULE);
                    return rule.toText();
                  } catch {
                    return 'Invalid RRULE';
                  }
                })()}
              </Typography>
            </Box>
          )}
        </Box>
        {/* Species/Breed Management UI - now on the right */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 350, maxWidth: 600, ml: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', columnGap: 4, mb: 2, width: '100%', minWidth: 700, maxWidth: 1400 }}>
            <Box>
              <Typography variant="subtitle1">Species & Breeds</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Species and breeds that this task applies to. If left empty it will apply to all species and breeds.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setAddMode(true)}
              disabled={addMode || isDeleting}
              sx={{ minWidth: 140 }}
            >
              Add Species
            </Button>
          </Box>
          <TableContainer sx={{ bgcolor: 'background.paper', borderRadius: 1, boxShadow: 0, border: '1px solid #eee', mt: 1, width: '100%', minWidth: 700, maxWidth: 1400 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Species & Breeds</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {addMode && (
                  <TableRow>
                    <TableCell align="right">
                      <SpeciesBreedSelector
                        onSelect={(speciesId, breedIds) => {
                          setAddSpeciesId(speciesId);
                          setAddBreedIds(breedIds);
                        }}
                        disabled={isDeleting}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          disabled={!addSpeciesId}
                          onClick={async () => {
                            if (addSpeciesId) {
                              await handleAddSpecies(addSpeciesId, addBreedIds);
                              setAddMode(false);
                              setAddSpeciesId(null);
                              setAddBreedIds([]);
                            }
                          }}
                          sx={{ mr: 1 }}
                        >
                          Save
                        </Button>
                        <Button onClick={() => { setAddMode(false); setAddSpeciesId(null); setAddBreedIds([]); }}>Cancel</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
                {taskSpecies.length === 0 && !addMode && (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No species or breeds assigned to this predefined task.
                    </TableCell>
                  </TableRow>
                )}
                {taskSpecies.length > 0 && taskSpecies.map(s => {
                  const speciesObj = allSpecies.find(sp => sp.id === s.speciesId);
                  const speciesName = speciesObj?.name || s.speciesId;
                  const breedNames = s.breedIds
                    .map(bid => speciesObj?.breeds?.find(b => b.breed.id === bid)?.breed.name || bid)
                    .join(', ');
                  const isEditing = editSpeciesId === s.speciesId;
                  return isEditing ? (
                    <TableRow key={s.speciesId}>
                      <TableCell align="right">
                        <SpeciesBreedSelector
                          onSelect={(_, breedIds) => setEditBreeds(breedIds)}
                          disabled={isDeleting}
                          initialSpeciesId={s.speciesId}
                          initialBreedIds={s.breedIds}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button sx={{ mr: 1 }} variant="contained" color="success" size="small" onClick={async () => { await handleUpdateSpecies(s.speciesId, editBreeds); setEditSpeciesId(null); }}>Save</Button>
                          <Button size="small" onClick={() => setEditSpeciesId(null)}>Cancel</Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={s.speciesId}>
                      <TableCell>
                        <Typography sx={{ mr: 2, display: 'inline-block' }}>Species: {speciesName}</Typography>
                        <Typography sx={{ mr: 2, display: 'inline-block' }}>Breeds: {breedNames}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Button size="small" color="primary" onClick={() => { setEditSpeciesId(s.speciesId); setEditBreeds(s.breedIds); }}>Edit</Button>
                          <Button size="small" color="error" onClick={() => handleRemoveSpecies(s.speciesId)}>Remove</Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
}

export default EditPredefinedTask;
